import { ifDefined } from 'lit/directives/if-defined.js';
import { live } from 'lit/directives/live.js';
import { ref } from 'lit/directives/ref.js';

import { watch } from '../utilities/watch';
import { twoWayBind } from '../utilities/twoWayBind';
import { html } from 'lit';
import { MaskParser } from '../utilities/maskedParser';
import { FloatField } from './float_field';
import { requiredValidator } from '../utilities/validators';

export class FloatMaskedField extends FloatField {
  static get properties() {
    return {
      prompt: { type: String },
      valueMode: { type: String, attribute: 'value-mode' },
      value: {},
      mask: { type: String },
      focused: { type: Boolean, state: true },
      maskedValue: { type: String, state: true },
      _mask: { type: String, state: true },
    };
  }

  constructor() {
    super();
    this.parser = new MaskParser();
    this.selection = { start: 0, end: 0 };
    this.compositionStart = 0;
    this._value = '';
    this.focused = false;
    this.maskedValue = '';
    this._mask = '';
    this.valueMode = 'raw';
    this.validators = [
      {
        ...requiredValidator,
        isValid: () => (this.required ? !!this._value : true),
      },
      {
        key: 'badInput',
        message: 'Does not match the required format',
        isValid: () => this.parser.isValidString(this.maskedValue),
      },
    ];

    twoWayBind(this, 'value', 'change', 'detail');
    watch(this, 'prompt', 'promptChange');
  }

  get value() {
    return this.valueMode !== 'raw'
      ? this.maskedValue
      : this._value?.toString() || '';
  }

  get inputSelection() {
    return {
      start: this.input.selectionStart || 0,
      end: this.input.selectionEnd || 0,
    };
  }

  get mask() {
    return this._mask;
  }

  set mask(value) {
    this._mask = value;
    this.parser.mask = value;
    this.parser.displayMask = value;
    if (this.value) {
      this.maskedValue = this.parser.apply(this._value?.toString());
    }
  }

  get emptyMask() {
    return this.parser.apply();
  }

  connectedCallback() {
    super.connectedCallback();
    this._mask = this._mask || this.parser.mask;
    this.prompt = this.prompt || this.parser.prompt;
    this.updateValidity();
  }

  setDefaultValue() {
    this._defaultValue = this._value;
  }

  updateFormValue() {
    this.valueMode === 'raw'
      ? this.setFormValue(this.value || null, this.value)
      : this.setFormValue(this.maskedValue || null, this.maskedValue);
    this.updateValidity();
    this.setInvalidState();
  }

  promptChange() {
    this.parser.prompt = this.prompt;
    if (this.value) {
      this.maskedValue = this.parser.apply(this._value?.toString());
    }
  }

  async updateInput(string, range) {
    const { value, end } = this.parser.replace(
      this.maskedValue,
      string,
      range.start,
      range.end
    );

    this.maskedValue = value;
    this._value = this.parser.parse(value);

    this.updateFormValue();
    this.requestUpdate();

    if (range.start !== this.mask.length) {
      this.emitEvent('input', { detail: this.value });
    }
    await this.updateComplete;

    this.input.setSelectionRange(end, end);
  }

  handleDragEnter() {
    if (!this.focused && !this._value) {
      this.maskedValue = this.emptyMask;
    }
  }

  handleDragLeave() {
    if (!this.focused) {
      this.updateMaskedValue();
    }
  }

  select() {
    this.input.select();
  }

  handleInput({ inputType, isComposing }) {
    const EMPTY = '';
    const value = this.input.value;
    const { start, end } = this.selection;
    const deleteEnd = this.parser.getNextNonLiteralPosition(end) + 1;

    if (inputType === 'deleteContentForward') {
      this.updateInput(EMPTY, { start, end: deleteEnd });
      return this.updateComplete.then(() =>
        this.input.setSelectionRange(deleteEnd, deleteEnd)
      );
    }

    if (inputType === 'deleteContentBackward' && !isComposing) {
      return this.updateInput(EMPTY, {
        start: this.parser.getPreviousNonLiteralPosition(
          this.inputSelection.start
        ),
        end,
      });
    }
    if (inputType === 'deleteByCut') {
      return this.updateInput(EMPTY, this.selection);
    }

    if (inputType === 'insertText') {
      return this.updateInput(
        value.substring(start, this.inputSelection.end),
        this.selection
      );
    }

    if (inputType === 'insertFromPaste') {
      return this.updateInput(value.substring(start, this.inputSelection.end), {
        start,
        end: this.inputSelection.start,
      });
    }

    if (inputType === 'insertFromDrop') {
      return this.updateInput(
        value.substring(this.inputSelection.start, this.inputSelection.end),
        { ...this.inputSelection }
      );
    }

    return undefined;
  }

  handleKeydown({ key }) {
    if (!key) return;
    this.selection = this.inputSelection;
  }

  async handleFocus() {
    this.focused = true;
    super.handleFocus();

    if (this.readOnly) return;

    if (!this._value) {
      this.maskedValue = this.emptyMask;
      await this.updateComplete;
      this.select();
    }
  }

  handleBlur() {
    this.focused = false;
    this.updateMaskedValue();
    this.invalid = !this.checkValidity();
    super.handleBlur();
  }

  handleCut() {
    this.selection = this.inputSelection;
  }

  handleDragStart() {
    this.selection = this.inputSelection;
  }

  handleCompositionStart() {
    this.compositionStart = this.inputSelection.start;
  }

  handleCompositionEnd({ data }) {
    this.updateInput(data, {
      start: this.compositionStart,
      end: this.inputSelection.end,
    });
  }

  handleClick() {
    const { selectionStart: start, selectionEnd: end } = this.input;
    if (start === end && start === this.maskedValue.length) {
      this.select();
    }
  }

  updateMaskedValue() {
    if (this.maskedValue === this.emptyMask) {
      this.maskedValue = '';
    }
  }

  _updateSetRangeTextValue() {
    this._value = this.parser.parse(this.maskedValue);
  }

  setSelectionRange(start, end, direction) {
    super.setSelectionRange(start, end, direction);
    this.selection = { start, end };
  }

  handleChange() {
    this._value = this.input.value;
    this.emitEvent('change', { detail: this.value });
  }

  setRangeText(replacement, start, end, selectMode) {
    const current = this.inputSelection;
    const _start = start ?? current.start;
    const _end = end ?? current.end;

    const result = this.parser.replace(
      this.maskedValue || this.emptyMask,
      replacement,
      _start,
      _end
    );
    this.maskedValue = this.parser.apply(this.parser.parse(result.value));
    this._updateSetRangeTextValue();

    this.updateComplete.then(() => {
      switch (selectMode) {
        case 'select':
          this.setSelectionRange(_start, _end);
          break;
        case 'start':
          this.setSelectionRange(_start, _start);
          break;
        case 'end':
          this.setSelectionRange(_end, _end);
          break;
        default:
          this.setSelectionRange(current.start, current.end);
      }
    });
  }

  renderInput() {
    return html`
      <input
        type="text"
        ${ref((el) => (this.input = el))}
        id=${this.id}
        name=${ifDefined(this.name)}
        autocomplete=${ifDefined(this.autocomplete)}
        .value=${live(this.maskedValue)}
        .placeholder=${live(this.placeholder ?? this.parser.escapedMask)}
        ?readonly=${this.readOnly}
        ?disabled=${this.disabled}
        @dragenter=${this.handleDragEnter}
        @dragleave=${this.handleDragLeave}
        @dragstart=${this.handleDragStart}
        @blur=${this.handleBlur}
        @focus=${this.handleFocus}
        @cut=${this.handleCut}
        @change=${this.handleChange}
        @click=${this.handleClick}
        @compositionstart=${this.handleCompositionStart}
        @compositionend=${this.handleCompositionEnd}
        @input=${this.handleInput}
        aria-invalid=${this.invalid ? 'true' : 'false'}
        @keydown=${this.handleKeydown}
      />
    `;
  }
}

window.customElements.get('float-masked-field') ||
  window.customElements.define('float-masked-field', FloatMaskedField);
